import { z } from "zod/v4";

import { AgenticLogicRuleDraftDtoSchema } from "../../../dtos/agentic-logic/rules";

/**
 * One `test(...)` call found in a rule's Vitest suite. `id` is the Vitest
 * full name (describe path + title, joined with " > "), so the editor can
 * point at a test and the adopt action can name it without shipping line
 * numbers back.
 *
 * Ids are stable for a given draft, not forever: a repeated path + title is
 * suffixed ` #n` in document order, so when the first occurrence is adopted
 * the next one's ` #2` becomes ` #1` — and a run that regenerates the suite
 * renumbers everything. The guard is the adopt body's `expectedLastUpdateTs`:
 * ids are only honoured against the draft they were listed from.
 */
export const AgenticLogicSuiteTestSchema = z.object({
  id: z.string(),
  title: z.string(),
  describePath: z.array(z.string()),
  /** 1-based, inclusive: the line of the `test(` call. */
  startLine: z.number().int().min(1),
  /** 1-based, inclusive: the line of the call's closing `)`. */
  endLine: z.number().int().min(1),
});
export type AgenticLogicSuiteTestType = z.infer<
  typeof AgenticLogicSuiteTestSchema
>;

// GET /agentic-logic/rules/{ruleId}/generated-tests

export const GetAgenticLogicRuleGeneratedTestsRequestPathParamsSchema =
  z.object({
    ruleId: z.uuid(),
  });
export type GetAgenticLogicRuleGeneratedTestsRequestPathParamsType = z.infer<
  typeof GetAgenticLogicRuleGeneratedTestsRequestPathParamsSchema
>;

export const GetAgenticLogicRuleGeneratedTestsResponseBodySchema = z.object({
  /** The draft's AI-generated suite; `null` until the first test run generates one. */
  suite: z.string().nullable(),
  tests: z.array(AgenticLogicSuiteTestSchema),
  /**
   * Whether the suite carries a reuse fingerprint for the current rule inputs.
   * False after an adoption or any explicit write: the next run re-verifies
   * or regenerates it.
   */
  fingerprintCurrent: z.boolean(),
});
export type GetAgenticLogicRuleGeneratedTestsResponseBodyType = z.infer<
  typeof GetAgenticLogicRuleGeneratedTestsResponseBodySchema
>;

// POST /agentic-logic/rules/{ruleId}/adopt-generated-tests
//
// Moves the named tests out of the AI-generated suite into the user suite,
// with the helpers and hooks they depend on. Once adopted a test is the
// user's: the generator reads the user suite and never recreates it.

export const AdoptAgenticLogicRuleGeneratedTestsRequestBodySchema = z.object({
  testIds: z.array(z.string().min(1)).min(1),
  /**
   * The draft `lastUpdateTs` the editor loaded its inventory from. The adopt
   * is a compare-and-set: a draft written since (a save, a run that
   * regenerated the suite, another adoption) is refused with `stale_draft`.
   */
  expectedLastUpdateTs: z.number().int().nonnegative(),
});
export type AdoptAgenticLogicRuleGeneratedTestsRequestBodyType = z.infer<
  typeof AdoptAgenticLogicRuleGeneratedTestsRequestBodySchema
>;

/**
 * The 409 body (`data`) an adopt returns instead of moving anything. Typed so
 * a client branches on `reason`, never on message text.
 */
export const AdoptAgenticLogicRuleGeneratedTestsConflictSchema =
  z.discriminatedUnion("reason", [
    z.object({
      reason: z.literal("stale_draft"),
      currentLastUpdateTs: z.number(),
    }),
    z.object({
      reason: z.literal("unknown_test_ids"),
      unknownTestIds: z.array(z.string()),
    }),
  ]);
export type AdoptAgenticLogicRuleGeneratedTestsConflictType = z.infer<
  typeof AdoptAgenticLogicRuleGeneratedTestsConflictSchema
>;

/**
 * The 422 body (`data`) the generated-tests GET and the adopt POST return
 * when a stored suite does not parse. The structural reads only trust a whole
 * tree, so the caller is told which suite is broken and where, 1-based like
 * an editor's gutter, rather than handed a partial listing.
 */
export const AgenticLogicMalformedSuiteErrorSchema = z.object({
  reason: z.literal("malformed_suite"),
  suite: z.enum(["generated", "user"]),
  diagnostics: z
    .array(
      z.object({
        message: z.string(),
        line: z.number().int().min(1),
        character: z.number().int().min(1),
      })
    )
    .min(1),
});
export type AgenticLogicMalformedSuiteErrorType = z.infer<
  typeof AgenticLogicMalformedSuiteErrorSchema
>;

export const AdoptAgenticLogicRuleGeneratedTestsResponseBodySchema = z.object({
  draft: AgenticLogicRuleDraftDtoSchema,
  generatedTests: z.array(AgenticLogicSuiteTestSchema),
  /** Inventory of the updated user suite, so the editor can reveal what moved. */
  userTests: z.array(AgenticLogicSuiteTestSchema),
  /** Ids (in the user suite) of the tests that were just adopted. */
  adoptedTestIds: z.array(z.string()),
});
export type AdoptAgenticLogicRuleGeneratedTestsResponseBodyType = z.infer<
  typeof AdoptAgenticLogicRuleGeneratedTestsResponseBodySchema
>;
